/**
 * Centralized color definitions for different entity types
 * Used across the application for consistency in highlighting, graphs, and UI elements
 */

export type EntityType = 'glossary' | 'document' | 'blueprint' | 'text' | 'type' | 'type-value' | 'variable' | 'variable-value' | 'dialog';

export const ENTITY_TYPE_COLORS: Record<string, string> = {
  glossary: '#8B5CF6', // Purple
  document: '#3B82F6', // Blue
  blueprint: '#10B981', // Green
  text: '#F59E0B', // Amber
  type: '#EF4444', // Red
  'type-value': '#EF4444', // Red (same as type)
  variable: '#F97316', // Orange
  'variable-value': '#F97316', // Orange (same as variable)
  dialog: '#14B8A6', // Teal
};

/**
 * Get the color for a specific entity type
 * @param type - The entity type
 * @returns The hex color string
 */
export function getEntityColor(type: string): string {
  return ENTITY_TYPE_COLORS[type] || '#6B7280'; // Default to gray if unknown
}

/**
 * Get a lighter version of the entity color for backgrounds
 * @param type - The entity type
 * @param opacity - Opacity value (0-1)
 * @returns The rgba color string
 */
export function getEntityColorWithOpacity(type: string, opacity: number = 0.1): string {
  const color = getEntityColor(type);
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Get a darker version of the entity color for hover states
 * @param type - The entity type
 * @returns The hex color string
 */
export function getEntityColorDark(type: string): string {
  const darkColors: Record<string, string> = {
    glossary: '#7C3AED', // Darker purple
    document: '#2563EB', // Darker blue
    blueprint: '#059669', // Darker green
    text: '#D97706', // Darker amber
    type: '#DC2626', // Darker red
    'type-value': '#DC2626', // Darker red
    variable: '#EA580C', // Darker orange
    'variable-value': '#EA580C', // Darker orange
    dialog: '#0F766E', // Darker teal
  };
  return darkColors[type] || '#4B5563'; // Default to darker gray if unknown
}

/**
 * Get the Tailwind CSS class name for the entity color
 * Useful for when you can use Tailwind classes instead of inline styles
 * @param type - The entity type
 * @returns The Tailwind color class string
 */
export function getEntityColorClass(type: string): string {
  const colorClasses: Record<string, string> = {
    glossary: 'text-purple-600',
    document: 'text-blue-500',
    blueprint: 'text-green-600',
    text: 'text-amber-500',
    type: 'text-red-500',
    'type-value': 'text-red-500',
    variable: 'text-orange-500',
    'variable-value': 'text-orange-500',
    dialog: 'text-teal-600',
  };
  return colorClasses[type] || 'text-gray-500';
}

/**
 * Get a human-readable label for the entity type
 * @param type - The entity type
 * @returns The display label
 */
export function getEntityTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    glossary: 'Object',
    document: 'Document',
    blueprint: 'Template',
    text: 'Text',
    type: 'Type',
    'type-value': 'Type Value',
    variable: 'Variable',
    'variable-value': 'Variable Value',
    dialog: 'Dialog',
  };
  return labels[type] || type;
}

